import { useEffect, useState } from 'react';
import { api } from '../services/api';

export interface FeaturePricing {
  cost: number;
  unit: string;
  label: string;
  recurring?: boolean;
}

export type PricingTable = Record<string, FeaturePricing>;

// Cache em memória (compartilhado entre montagens) — a tabela de preços muda raramente.
let cachedPricing: PricingTable | null = null;
let cachedConnectionBilling = false;
let inflight: Promise<PricingTable> | null = null;

async function loadPricing(): Promise<PricingTable> {
  if (cachedPricing) return cachedPricing;
  if (inflight) return inflight;
  inflight = api.getCreditPricing()
    .then(res => {
      cachedPricing = res.data || {};
      cachedConnectionBilling = !!(res as any).connectionBillingEnabled;
      return cachedPricing;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

// Invalida o cache de preços (chamar após o superadmin editar um preço,
// para os avisos de custo refletirem o novo valor sem recarregar a página).
export function invalidateCreditPricingCache() {
  cachedPricing = null;
}

// Formata créditos em pt-BR com até 3 casas (oculta zeros à direita).
export function formatCredits(value: number): string {
  if (value == null || Number.isNaN(value)) return '0';
  return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

export function useCreditPricing() {
  const [pricing, setPricing] = useState<PricingTable | null>(cachedPricing);
  const [connectionBillingEnabled, setConnectionBillingEnabled] = useState(cachedConnectionBilling);
  const [loading, setLoading] = useState(!cachedPricing);

  useEffect(() => {
    let alive = true;
    if (cachedPricing) { setPricing(cachedPricing); setLoading(false); return; }
    loadPricing()
      .then(p => { if (alive) { setPricing(p); setConnectionBillingEnabled(cachedConnectionBilling); } })
      .catch(() => { if (alive) setPricing({}); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Custo total de uma feature × quantidade.
  const getCost = (featureKey: string, qty = 1): number => {
    const meta = pricing?.[featureKey];
    if (!meta) return 0;
    return Math.round(meta.cost * qty * 10000) / 10000;
  };

  const getMeta = (featureKey: string): FeaturePricing | null => pricing?.[featureKey] || null;

  return { pricing, loading, getCost, getMeta, formatCredits, connectionBillingEnabled };
}
